'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Container } from '../container';
import { HorizontalScrollbar } from '../horizontal-scrollbar';

function ProductListingRoot({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={cn('grid gap-10 overflow-hidden', className)}
      {...props}
    >
      {children}
    </section>
  );
}

function ProductListingHeader({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <Container
      className={cn(
        'flex w-full items-center justify-between text-[var(--text-color)]',
        className
      )}
      {...props}
    >
      {children}
    </Container>
  );
}

function ProductListingItems({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'relative flex snap-x snap-mandatory flex-nowrap gap-3 overflow-x-auto pb-8',
        'px-[var(--container-padding)]',
        'md:scroll-pl-[max(calc((100%-var(--layout-page-width))/2+var(--container-padding)))]',
        'md:pl-[max(calc((100%-var(--layout-page-width))/2+var(--container-padding)))]',
        'md:pr-[max(calc((100%-var(--layout-page-width))/2+var(--container-padding)))]',
        'scrollbar-none',
        className
      )}
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      data-scroll-items=""
      {...props}
    >
      <style jsx>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {children}
    </div>
  );
}

// Create a wrapper for individual items
function ProductListingItem({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'relative flex w-[calc(100vw/2)] min-w-[180px] shrink-0 flex-col items-stretch space-y-1 overflow-hidden md:w-[calc(var(--layout-page-width)/6)]',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function ProductListingFooter({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return <HorizontalScrollbar className={className} {...props} />;
}

export function ProductListing({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLElement>) {
  return (
    <ProductListingRoot className={className} {...props}>
      {children}
    </ProductListingRoot>
  );
}

ProductListing.Root = ProductListingRoot;
ProductListing.Header = ProductListingHeader;
ProductListing.Items = ProductListingItems;
ProductListing.Item = ProductListingItem;
ProductListing.Footer = ProductListingFooter;
